import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import { DateTime, formatToISODate, getDateTime } from 'infras/datetime';
import { get, post } from 'infras/rest_client';
import { AssistantSchedulePostBody } from 'integrations/assistant/presentations/api/schema/assistant';
import { marked } from 'marked';
import { ProjectItem } from 'presentations/api/schema/project';
import { TeamUserListItem } from 'presentations/api/schema/team_user';
import {
  UserListItem,
  UserProfileItem,
  ViewerItem,
} from 'presentations/api/schema/user';
import {
  ActiveIndex,
  AssistantWidgetContext,
  setActiveIndex,
} from 'presentations/reducers/AssistantWidget';
import {
  DashboardPortalContext,
  setErrorMessage,
} from 'presentations/reducers/DashboardPortal';
import { apiPath } from 'presentations/routes/path';
import { setEvent } from 'presentations/views/helpers/analytics';
import { useRequest } from 'presentations/views/hooks/request';
import {
  SyntheticEvent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

interface Props {
  viewer: ViewerItem;
}

export const useScheduleView = (props: Props) => {
  const { state, dispatch } = useContext(AssistantWidgetContext);
  const t = translateInComponent([translations]);
  const portalContext = useContext(DashboardPortalContext);
  const [isLoaded, setIsLoaded] = useState<boolean>(true);

  const [startedAtFilter, setStartedAtFilter] = useState<DateTime | undefined>(
    undefined,
  );
  const [endedAtFilter, setEndedAtFilter] = useState<DateTime | undefined>(
    undefined,
  );

  const [teamUsers, setTeamUsers] = useState<TeamUserListItem[]>([]);
  const [content, setContent] = useState<string | null>(null);
  const [messageStream, setMessageStream] = useState<string | null>(null);
  const [messages, setMessages] = useState<
    { role: 'system' | 'user'; content: string }[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [selectedUserUids, setSelectedUserUids] = useState<string[]>([
    props.viewer.uid,
  ]);

  const [selectableUsers, setSelectableUsers] = useState<TeamUserListItem[]>(
    [],
  );

  const [projectUid, setProjectUid] = useState<string>('');
  const isSelectedProject = projectUid.length > 0;
  const projectRequest = useRequest(
    isSelectedProject
      ? apiPath(`/v1/teams/${props.viewer.team!.uid}/projects/${projectUid}`)
      : null,
  );
  const project: ProjectItem | null = projectRequest.data;

  const userProfileRequest = useRequest(apiPath('/v1/user/profile'));
  const userProfile: UserProfileItem = userProfileRequest.data;

  const isProjectLoading = isSelectedProject && !projectRequest.data;
  const projectMembers: UserListItem[] | null = project ? project.users : null;
  const isCheckedAllMember = useMemo(() => {
    if (selectableUsers.every((m) => selectedUserUids.includes(m.userId))) {
      return true;
    }
    return false;
  }, [selectedUserUids, selectableUsers]);

  useEffect(() => {
    if (isLoaded) return;
    onSubmit();
  }, [isLoaded]);

  const fetchTeamUsers = async () => {
    try {
      const result = await get(
        apiPath(`/v1/teams/${props.viewer.team!.uid}/users?perPage=999`),
      );
      const users: TeamUserListItem[] = result.data ?? [];
      setTeamUsers(users);
      setSelectableUsers(users);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    fetchTeamUsers();
  }, []);

  useEffect(() => {
    if (!project) return;

    const newUids = project.users
      .filter(
        (u) => selectedUserUids.includes(u.uid) && u.uid !== props.viewer.uid,
      )
      .map((u) => u.uid);
    if (selectedUserUids.includes(props.viewer.uid)) {
      newUids.push(props.viewer.uid);
    }
    setSelectedUserUids(newUids);
  }, [project?.uid]);

  useEffect(() => {
    setSelectableUsers(
      ((isProjectLoading ? [] : projectMembers) || teamUsers || [])
        .map((user) => {
          const uid = isTeamUserListItem(user) ? user.userId : user.uid;
          const teamUser = teamUsers?.find((u) => u.userId === uid);
          return teamUser!;
        })
        .filter((u) => u !== undefined),
    );
  }, [isProjectLoading, projectMembers]);

  const isTeamUserListItem = (user: any): user is TeamUserListItem => {
    return user.userId !== undefined;
  };

  const onSelectProject = (e: SyntheticEvent<HTMLInputElement>) => {
    const uid = e.currentTarget.value;
    setProjectUid(uid);
  };

  // 日程調整用パラメータの初期化
  useEffect(() => {
    const startedAt = getDateTime();
    setStartedAtFilter(startedAt);
    setEndedAtFilter(startedAt.plus({ month: 1 }));
  }, []);

  const onChangeCheckedAllMember = () => {
    let newUids: string[] = [];
    // 未チェック状態からの場合は、全チェックする
    if (!isCheckedAllMember) {
      if (projectMembers) {
        newUids = (teamUsers || [])
          .filter((u) => projectMembers.some((m) => m.uid === u.userId))
          .map((u) => u.userId);
      } else {
        newUids = (teamUsers || []).map((u) => u.userId);
      }
    }
    // 自分は別枠
    if (selectedUserUids.includes(props.viewer.uid)) {
      newUids.push(props.viewer.uid);
    }
    setSelectedUserUids(newUids);
  };

  const onSelectUser = (uid: string) => {
    let newUids: string[];
    if (selectedUserUids.some((u) => u === uid)) {
      newUids = selectedUserUids.filter((u) => u !== uid);
    } else {
      newUids = [...selectedUserUids, uid];
    }
    setSelectedUserUids(newUids);
  };

  const onChangeMessage = (e: SyntheticEvent<HTMLInputElement>) => {
    setContent(e.currentTarget.value);
  };

  const createMessageStream = async (text: string) => {
    const strings = text.split('');
    let duration = 0;
    let stream = '';
    for (let i = 0; i < strings.length; i++) {
      const random = Math.floor(Math.random() * 4);
      duration += random * 20;
      setTimeout(() => {
        stream += strings[i];
        setMessageStream(marked(stream));
      }, duration);
    }
    setTimeout(() => {
      setMessages([...messages, { role: 'system', content: marked(text) }]);
      setMessageStream(null);
    }, duration + 1000);
  };

  const onSubmitMessage = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!content) return;
    setMessages([...messages, { role: 'user', content }]);
    setContent('');
    setIsLoaded(false);
    setEvent('submit chat assistant add message', {
      view: 'schedule',
    });
  };

  const onClickStartScheduling = () => {
    dispatch(setActiveIndex(ActiveIndex.ScheduleChatMessage));
    setIsLoaded(false);
  };

  const onClickEditUser = () => {
    dispatch(setActiveIndex(ActiveIndex.ScheduleChatSelectUser));
  };

  const onChangeDateFilter = (startedAt?: Date, endedAt?: Date) => {
    if (startedAt) {
      setStartedAtFilter(getDateTime(startedAt, userProfile.timeZone));
    }
    if (endedAt) {
      setEndedAtFilter(getDateTime(endedAt, userProfile.timeZone));
    }
  };

  const onClickBack = () => {
    dispatch(setActiveIndex(ActiveIndex.ScheduleChat));
  };

  const onSubmit = async () => {
    if (isLoading) return;
    if (!startedAtFilter || !endedAtFilter) return;
    const data: AssistantSchedulePostBody = {
      startedAt: startedAtFilter.toMillis(),
      endedAt: endedAtFilter.plus({ day: 1 }).toMillis(),
      userIds: selectedUserUids,
      messages,
    };

    setIsLoading(true);

    const result = await post(apiPath(`/v1/integrations/assistant/schedule`), {
      data,
      timeout: 60 * 1 * 1000, // 1 min
    });
    if (!result.isSuccess) {
      setIsLoading(false);
      setIsLoaded(true);
      portalContext.dispatch(setErrorMessage(t('Update error')));
      return;
    }
    await createMessageStream(result.data.answer);
    setIsLoading(false);
    setIsLoaded(true);
  };

  const sortedSelectableUsers = [
    ...selectableUsers,
  ].sort((a: TeamUserListItem, b: TeamUserListItem) =>
    a.userId === props.viewer.uid ? -1 : 1,
  );

  const selectedUsers = [...selectableUsers].filter((user: TeamUserListItem) =>
    selectedUserUids.includes(user.userId),
  );

  return {
    state: {
      content,
      isNoContent: !Boolean(content),
      messages,
      isLoading: !isLoaded || isLoading,
      teamUsers,
      selectedUserUids,
      projectUid,
      isCheckedAllMember,
      isProjectLoading,
      isLoadingTeamUsers: !teamUsers,
      projectMembers,
      startedAtFilter,
      endedAtFilter,
      activeIndex: state.activeIndex,
      selectableUsers: sortedSelectableUsers,
      selectedUsers,
      userProfile,
      projects: portalContext.state.projects,
      messageStream,
    },
    actions: {
      onClickBack,
      onSelectUser,
      onChangeCheckedAllMember,
      onChangeMessage,
      onSubmitMessage,
      onSelectProject,
      onClickStartScheduling,
      onClickEditUser,
      onChangeDateFilter,
    },
  };
};
